import { OnDestroy, AfterViewInit } from '@angular/core';
import { FacilitiesService } from '../../core/facilities/facilities.service';
import Highcharts from 'highcharts';
import * as api from '../../api';
import * as Colors from '../colors';
import { AbstractSummaryComponent } from './abstract-summary.component';
import { AggregateData } from "../../api/model/aggregateData";
import { GREY } from "../colors";
import { ChartType, TrafficLightColorChartType } from "../chart-options-factory";

/**
 * Base class for Medication Summary widgets and pages.
 */
export abstract class AbstractAggregateSummaryComponent extends AbstractSummaryComponent implements AfterViewInit, OnDestroy {

    /** Construct */
    constructor(facilitiesSvc: FacilitiesService) {
        super(facilitiesSvc);
    }

    /**
     * Transform AggregateData[] from API into a more useful structure for drawing column and bar charts.
     */
    protected transformAggregateData(apiData: api.AggregateData[], CustomValue: boolean = false, TotalMinMax: boolean = false): TransformedAggregate {
        let intermediate = new TransformedAggregate(apiData);


        let allSubGroups: string[] = [];
        apiData.filter(x => x.SubGroups).forEach(g => {
            let mySubGroups = g.SubGroups.map(x => x.GroupName);
            mySubGroups.forEach(function (y) {
                if (allSubGroups.indexOf(y) == -1) {
                    allSubGroups.push(y);
                }
            });
        });

        for (let outerGroupIdx = 0; outerGroupIdx < apiData.length; ++outerGroupIdx) {
            let outerGroup = apiData[outerGroupIdx];
            intermediate.outerGroupNames.push(outerGroup.GroupName);
            //If TotalMaxMin is True then include subgroup 
            if (!outerGroup.SubGroups || TotalMinMax) {
                let points = intermediate.countPerOuterGroupIndex.get(outerGroup.GroupName);
                if (!points) {
                    // First time seeing this group
                    points = new Array(apiData.length).fill(0);
                    intermediate.countPerOuterGroupIndex.set(outerGroup.GroupName, points);
                }
                
                if(CustomValue) {
                    points[outerGroupIdx] = outerGroup.CustomValue;
                }
                else if(outerGroup.TotalMax != null)
                {
                    points[outerGroupIdx] = outerGroup.TotalMax;
                }
                else if(outerGroup.TotalMin != null)
                {
                    points[outerGroupIdx] = outerGroup.TotalMin;
                }
                else{
                    points[outerGroupIdx] = outerGroup.ItemCount;
                }
              


                continue;
            }

            // If a given record does not have a subgroup, add it. See TP-82191
            let currentGroupNames = outerGroup.SubGroups.map(y => y.GroupName);
            allSubGroups.forEach(x => {
                if (currentGroupNames.indexOf(x) < 0) {
                    outerGroup.SubGroups.push({ ItemCount: 0, CustomValue: 0, GroupName: x, SubGroups: [], SortDescending: false });
                }
            });

            // Since the order of any given stack is irrelevant, we force the column to have single order in case the order from the server 
            // is different across widgets. See TP-82191

            let sortedGroupNames = null;

            if (!outerGroup.SortDescending) {
                sortedGroupNames = outerGroup.SubGroups.sort((a: AggregateData, b: AggregateData) => {
                    let av = a.GroupName.toLowerCase();
                    let bv = b.GroupName.toLowerCase();
                    // if a.SortLast is true, the subgroup will be sorted to make them appear at the top of the bar charts
                    let comp = a.SortLast ? 1 : av < bv ? -1 : av > bv ? 1 : 0;
                    return comp;
                });
            } else if (outerGroup.SortDescending) {
                sortedGroupNames = outerGroup.SubGroups.sort((a: AggregateData, b: AggregateData) => {
                    let av = a.GroupName.toLowerCase();
                    let bv = b.GroupName.toLowerCase();
                    let comp = a.SortLast ? 1 : av > bv ? -1 : av < bv ? 1 : 0;
                    return comp;
                });
            }

            for (let innerGroup of sortedGroupNames) {


                let points = intermediate.innerCountPerOuterGroupIndex.get(innerGroup.GroupName);
                if (!points) {
                    // First time seeing this inner group
                    intermediate.innerGroupNames.push(innerGroup.GroupName);
                    points = new Array(sortedGroupNames.length).fill(0)
                    intermediate.innerCountPerOuterGroupIndex.set(innerGroup.GroupName, points);
                }
                if (CustomValue) {
                    points[outerGroupIdx] = innerGroup.CustomValue;
                }
                else {
                    points[outerGroupIdx] = innerGroup.ItemCount;
                }

            }

        }
        return intermediate;
    }

    /**
     * Populate a donut chart with resident count & aggregate data.
     * @param customValue for all resident charts the customValue is used as a default value for the distinct residents count, 
     * some charts might not have customValue, in this case we still need to use ItemCount
     */
    protected populateResidentCountDonut(
        chart: Highcharts.Chart,
        residentCount: api.ResidentCountResult,
        colors?: string[],
        customValue: boolean = false
    ): void {
        let aggregateData = residentCount.ItemGroups;

        this.populateDonut(chart, aggregateData, colors, customValue);

        this.setSubtitle(chart, aggregateData ? residentCount.Percentage + "%" : "No Data", true);
        this.redrawChart(chart);
    }

    /**
     * Populate a donut chart with resident count & aggregate data.
     */
    protected populateResidentCountDonutWithCustomCaption(
        chart: Highcharts.Chart,
        residentCount: api.ResidentCountResult,
        overrideColors?: string[],
        customValue: boolean = false
    ): void {
        let aggregateData = residentCount.ItemGroups;

        this.populateDonut(chart, aggregateData, overrideColors, customValue);

        this.setSubtitle(chart, aggregateData ? residentCount.CustomCaption : "No Data", true);
        this.redrawChart(chart);
    }

    protected populateAverageAreaOrders(chart: Highcharts.Chart, OTCList: string[], RxList: string[]) {
        chart.addSeries({ type: 'pie', name: "OTC", data: [null, 2, 5, 1, 4, 3] });

        chart.addSeries({ type: 'pie', name: "Rx", data: [null, 1, 3, 0, 2, 1] });
    }

    /**
     * 
     * @param useCustomValueCount When it's true, CustomValue overrides the ItemsCount value. Ex: We are showing CustomValue as Distinct resident count instead of ItemCount
     */

    private populateDonut(chart: Highcharts.Chart, aggregateData: api.AggregateData[], overrideColors?: string[], useCustomValueCount: boolean = false ) {

        if (aggregateData) {
            let series = chart.addSeries({ type: 'pie', name: ' ' });

            let colors: string[];
            if (overrideColors) {
                colors = overrideColors;
            }
            else {
                switch (aggregateData.length) {
                    case 1:
                        colors = Colors.DONUT1_CHART_COLORS;
                        break;
                    case 2:
                        colors = Colors.DONUT2_CHART_COLORS;
                        break;
                    case 3:
                        colors = Colors.DONUT3_CHART_COLORS;
                        break;
                    default:
                        colors = Colors.DONUT_CHART_COLORS;
                        break;
                }
            }

            let colorIdx = 0;
            for (let outterGroup of aggregateData) {
                if (outterGroup.ItemCount) {
                    series.addPoint({
                        name: outterGroup.GroupName,
                        y: useCustomValueCount ? outterGroup.CustomValue : outterGroup.ItemCount,
                        color: colors[colorIdx]
                    }, true);
                }
                ++colorIdx;
            }
        }

        chart.hideLoading();
    }

    protected populateDecimalDonut(chart: Highcharts.Chart, data: api.AggregateData[], percentage: number): void {
        let aggregateData = data;


        if (aggregateData) {
            let series = chart.addSeries({ type: 'pie', name: ' ' });

            let colors: string[];
            switch (aggregateData.length) {
                case 1:
                    colors = Colors.DONUT1_CHART_COLORS;
                    break;
                case 2:
                    colors = Colors.DONUT2_CHART_COLORS;
                    break;
                case 3:
                    colors = Colors.DONUT3_CHART_COLORS;
                    break;
                default:
                    colors = Colors.DONUT_CHART_COLORS;
                    break;
            }

            let colorIdx = 0;
            for (let outterGroup of aggregateData) {
                if (outterGroup.ItemCount) {
                    let color = colors[colorIdx];
                    series.addPoint({
                        name: outterGroup.GroupName,
                        y: outterGroup.ItemCount,
                        color: color
                    }, true);
                }
                ++colorIdx;
            }
        }
        chart.hideLoading();

        this.setSubtitle(chart, aggregateData ? percentage / 100 + "" : "No Data", true);
        this.redrawChart(chart);
    }

    protected populateSemiCircleDonut(chart: Highcharts.Chart, aggregateData: api.AggregateData[]): void {
        if (aggregateData) {
            const sum = aggregateData.reduce((sum, x) => sum + x.ItemCount, 0);
            if (sum) {
                let series = chart.addSeries({ type: 'pie', name: ' ' });

                for (let outerGroup of aggregateData) {
                    series.addPoint({
                        name: outerGroup.GroupName,
                        y: outerGroup.ItemCount
                    }, true);
                }
            }
            else {
                this.setSubtitle(chart, 'No Data', true);
            }
        }

        chart.hideLoading();
        this.redrawChart(chart);
    }

    protected populateSemiCircleDonutDecimal(chart: Highcharts.Chart, residentCount: api.ResidentCountResult): void {
        let aggregateData = residentCount.ItemGroups;

        if (aggregateData) {
            const sum = aggregateData.reduce((sum, x) => sum + x.ItemCount, 0);
            if (sum) {
                let series = chart.addSeries({ type: 'pie', name: ' ' });

                for (let outerGroup of aggregateData) {
                    series.addPoint({
                        name: outerGroup.GroupName,
                        y: outerGroup.ItemCount
                    }, true);
                }
                let subtitle: Highcharts.SubtitleOptions = { text: residentCount.Percentage + "%" };
                chart.setTitle(null, subtitle);
            }
            else {
                this.setSubtitle(chart, 'No Data', true);
            }
        }

        chart.hideLoading();
        this.redrawChart(chart);
    }

    /**
     * Populate a donut chart with resident count & aggregate data.
     */
    protected populateResidentCountDecimalDonut(
        chart: Highcharts.Chart,
        residentCount: api.ResidentCountResult,
        decimalValue: number = (residentCount.Percentage / 100)
    ): void {
        let aggregateData = residentCount.ItemGroups;


        if (aggregateData) {
            let series = chart.addSeries({type: 'pie', name: ' ' });

            let colors: string[];
            switch (aggregateData.length) {
                case 1:
                    colors = Colors.DONUT1_CHART_COLORS;
                    break;
                case 2:
                    colors = Colors.DONUT2_CHART_COLORS;
                    break;
                case 3:
                    colors = Colors.DONUT3_CHART_COLORS;
                    break;
                default:
                    colors = Colors.DONUT_CHART_COLORS;
                    break;
            }

            let colorIdx = 0;
            for (let outterGroup of aggregateData) {
                if (outterGroup.ItemCount) {
                    let color = colors[colorIdx];
                    if (outterGroup.GroupName === '0 Orders') {
                        color = GREY;
                    }
                    series.addPoint({
                        name: outterGroup.GroupName,
                        y: outterGroup.ItemCount,
                        color: color
                    }, true);
                }
                ++colorIdx;
            }
        }
        chart.hideLoading();

        this.setSubtitle(chart, aggregateData && !isNaN(decimalValue) ? decimalValue + "" : "No Data", true);
        this.redrawChart(chart);
    }

    protected populateControlledMedFillCountDecimalDonut(
        chart: Highcharts.Chart,
        fillCount: api.ControlledMedFillCountResult
    ): void {

    }

    protected populateMedSummaryCountDonut(
        chart: Highcharts.Chart,
        residentCount: api.MedicationSummaryCountResult,
        CustomValue: boolean = false
    ): void {

        let aggregateData = residentCount.ItemGroups;

        if (aggregateData) {
            let series = chart.addSeries({ type: 'pie', name: ' ' });

            let colors: string[];
            switch (aggregateData.length) {
                case 1:
                    colors = Colors.DONUT1_CHART_COLORS;
                    break;
                case 2:
                    colors = Colors.DONUT2_CHART_COLORS;
                    break;
                case 3:
                    colors = Colors.DONUT3_CHART_COLORS;
                    break;
                default:
                    colors = Colors.DONUT_CHART_COLORS;
                    break;
            }

            let colorIdx = 0;
            for (let outterGroup of aggregateData) {
                if (outterGroup.ItemCount) {
                    series.addPoint({
                        name: outterGroup.GroupName,
                        y: CustomValue ? outterGroup.CustomValue : outterGroup.ItemCount,
                        color: colors[colorIdx]
                    }, true);
                }
                ++colorIdx;
            }
        }

        chart.hideLoading();
        this.setSubtitle(chart, aggregateData ? residentCount.Percentage + "%" : "No Data", true);
        this.redrawChart(chart);
    }

    /**
     * Populate a treemap chart with resident count & aggregate data.
     */
    protected populateResidentCountTreemap(
        chart: Highcharts.Chart,
        residentCount: api.ResidentCountResult,
        subtitleLabel: string
    ): void {
        let aggregateData = residentCount.ItemGroups;
        let data = [];

        // Now for each medication group
        let colorIdx = 0;
        for (let outterGroup of aggregateData) {
            if (outterGroup.ItemCount === 0)
                continue;

            data.push({
                id: outterGroup.GroupName,
                name: outterGroup.GroupName,
                color: Colors.TREEMAP_CHART_COLORS[colorIdx++]
            });
            data.push({
                name: Math.round(outterGroup.ItemCount / residentCount.DistinctResidents * 100) + "%",
                parent: outterGroup.GroupName,
                value: outterGroup.ItemCount
            });
        }

        chart.hideLoading();
        let percentage = Math.round(residentCount.DistinctResidents / residentCount.ResidentCount * 1000) / 10;
        chart.setTitle(null, { text: percentage + subtitleLabel });
        chart.series[0].setData(data);
    }

    /**
     * Populate a treemap chart with resident count & aggregate data.
     */
    protected getAggregateList(
        rawData: AggregateData[]
    ): TransformedAggregate[] {

        // tslint:disable-next-line:typedef-whitespace
        let transformedAggregate: TransformedAggregate[];
        for (let data of rawData) {
            transformedAggregate.push(
                this.transformAggregateData(data[0], true)
            );
        }
        return transformedAggregate;
    }

    /**
     * Populate a column chart without aggregate data in a single color (orange).
     */
    protected populateSingleColorColumns(chart: Highcharts.Chart, aggregateData: TransformedAggregate): void {
        chart.options.colors = [Colors.ORANGE];
        chart.options.legend.enabled = false;
        this.populateBars(chart, aggregateData);
    }

    /**
     * Populate a column chart without aggregate data.
     */
    protected populateColumns(chart: Highcharts.Chart, aggregateData: TransformedAggregate): void {
        this.populateBars(chart, aggregateData);
    }

    /**
     * Populate a column chart with aggregate data.
     */
    protected populateAggregateColumns(chart: Highcharts.Chart, aggregateData: TransformedAggregate): void {
        this.populateAggregateBars(chart, aggregateData);
    }

    /**
     * Populate a column chart with aggregate data using donut color scheme.
     */
    protected populateAggregateColumnsWithDonutColors(chart: Highcharts.Chart, aggregateData: TransformedAggregate): void {
        this.populateAggregateBarsWithDonutColors(chart, aggregateData);
    }

    /**
     * Populate a bar chart with aggregate data.
     * if useOwnColors is true,
     * then colors and ordering defined in the caller will be applied instead of default colors.
     */
    protected populateAggregateBars(chart: Highcharts.Chart, aggregateData: TransformedAggregate, useOwnColors?: boolean): void {
        chart.xAxis[0].setCategories(aggregateData.outerGroupNames);

        if (!useOwnColors) {
            chart.options.colors = this.getAreaChartColorSystem(aggregateData.innerGroupNames.length, ChartType.Bar);
        }

        let numDataPoints = aggregateData.outerGroupNames.length;
        for (let innerName of aggregateData.innerGroupNames) {
            chart.addSeries({
                type: 'column', // stacked bar chart
                name: innerName,
                data: aggregateData.innerCountPerOuterGroupIndex.get(innerName).slice(0, numDataPoints)
            }, false);
        }

        chart.hideLoading();
        let haveData = aggregateData.outerGroupNames.length > 0;
        this.setSubtitle(chart, haveData ? '' : 'No Data', !haveData);
        this.redrawChart(chart);
    }

    /**
     * Populate a bar chart with aggregate data.
     */
     protected populateAggregateBarsWithTrafficLightColors(chart: Highcharts.Chart, aggregateData: TransformedAggregate, 
                                                           trafficLightColorChartType: TrafficLightColorChartType): void {

        chart.xAxis[0].setCategories(aggregateData.outerGroupNames);

        switch(trafficLightColorChartType){
            case TrafficLightColorChartType.TotalDailyDoseOfAcetaminophen: {
                chart.options.colors = Colors.TRAFFIC_LIGHT_COLORS;
            }break;
            case TrafficLightColorChartType.MorphineEquivalentForOpioids: {
                chart.options.colors = Colors.TRAFFIC_LIGHT_PLUS_COLORS;
            }break;
            default:{}
        }
        
        
        let numDataPoints = aggregateData.outerGroupNames.length;
        for (let innerName of aggregateData.innerGroupNames) {
            chart.addSeries({
                type: 'bar',
                name: innerName,
                data: aggregateData.innerCountPerOuterGroupIndex.get(innerName).slice(0, numDataPoints)
            }, false);
        }

        chart.hideLoading();
        let haveData = aggregateData.outerGroupNames.length > 0;
        this.setSubtitle(chart, haveData ? '' : 'No Data', !haveData);
        this.redrawChart(chart);
    }

    /**
     * Populate a bar chart with aggregate data using donut color scheme.
     */
    protected populateAggregateBarsWithDonutColors(chart: Highcharts.Chart, aggregateData: TransformedAggregate): void {
        chart.xAxis[0].setCategories(aggregateData.outerGroupNames);
        let numDataPoints = aggregateData.outerGroupNames.length;

        if (aggregateData && aggregateData.innerGroupNames) {
            chart.options.colors = this.getAreaChartColorSystem(aggregateData.innerGroupNames.length, ChartType.Donut);
        }

        for (let innerName of aggregateData.innerGroupNames) {
            chart.addSeries({
                type: 'bar',
                name: innerName,
                data: aggregateData.innerCountPerOuterGroupIndex.get(innerName).slice(0, numDataPoints)
            }, false);
        }

        chart.hideLoading();
        let haveData = aggregateData.outerGroupNames.length > 0;
        this.setSubtitle(chart, haveData ? '' : 'No Data', !haveData);
        this.redrawChart(chart);
    }

    /**
     * Populate a bar chart with aggregate data sorted so the max value group is always first.
     */
    protected populateAggregateBarsSorted(chart: Highcharts.Chart, aggregateData: TransformedAggregate): void {
        chart.xAxis[0].setCategories(aggregateData.outerGroupNames);
        let numDataPoints = aggregateData.outerGroupNames.length;
        let totalValues = [];
        for (let innerName of aggregateData.innerGroupNames) {
            // Get the total of array elements for each inner group
            const reducer = (acc, curr) => acc + curr,
                totalValue = aggregateData.innerCountPerOuterGroupIndex.get(innerName).slice(0, numDataPoints).reduce(reducer);
            totalValues.push([totalValue, innerName]);
        }

        let sortedTotalValues = totalValues.sort(function (a, b) { return b[0] - a[0] });

        // Add to chart in descending order of total
        for (let innerName of sortedTotalValues) {
            chart.addSeries({
                type: 'bar',
                name: innerName[1],
                data: aggregateData.innerCountPerOuterGroupIndex.get(innerName[1]).slice(0, numDataPoints)
            }, false);
        }

        chart.hideLoading();
        let haveData = aggregateData.outerGroupNames.length > 0;
        this.setSubtitle(chart, haveData ? '' : 'No Data', !haveData);
        this.redrawChart(chart);
    }

    /**
     * Populate a bar chart with aggregate data sorted so the max value group is always first using donut color scheme.
     */
    protected populateAggregateBarsSortedWithDonutColors(chart: Highcharts.Chart, aggregateData: TransformedAggregate): void {
        chart.xAxis[0].setCategories(aggregateData.outerGroupNames);
        let numDataPoints = aggregateData.outerGroupNames.length;

        if (aggregateData && aggregateData.innerGroupNames) {
            chart.options.colors = this.getAreaChartColorSystem(aggregateData.innerGroupNames.length, ChartType.Donut);
        }

        let totalValues = [];
        for (let innerName of aggregateData.innerGroupNames) {
            if (innerName.toUpperCase().substring(0, 5) !== "OTHER") {
                // Get the total of array elements for each inner group
                const reducer = (acc, curr) => acc + curr,
                    totalValue = aggregateData.innerCountPerOuterGroupIndex.get(innerName).slice(0, numDataPoints).reduce(reducer);
                totalValues.push([totalValue, innerName]);
            }
            else {
                totalValues.push([0, innerName]);
            }
        }

        let sortedTotalValues = totalValues.sort(function (a, b) { return b[0] - a[0] });

        // Add to chart in descending order of total
        for (let innerName of sortedTotalValues) {
            chart.addSeries({
                type: 'column',
                name: innerName[1],
                data: aggregateData.innerCountPerOuterGroupIndex.get(innerName[1]).slice(0, numDataPoints)
            }, false);
        }

        chart.hideLoading();
        let haveData = aggregateData.outerGroupNames.length > 0;
        this.setSubtitle(chart, haveData ? '' : 'No Data', !haveData);
        this.redrawChart(chart);
    }

    /**
     * Populate a bar chart without aggregate data.
     */
    protected populateBars(chart: Highcharts.Chart, aggregateData: TransformedAggregate, noDataMessage?: string): void {
        chart.xAxis[0].setCategories(aggregateData.outerGroupNames);
        let numDataPoints = aggregateData.outerGroupNames.length;
        for (let innerName of aggregateData.outerGroupNames) {
            chart.addSeries({
                type: 'bar',
                name: innerName,
                data: aggregateData.countPerOuterGroupIndex.get(innerName).slice(0, numDataPoints)
            }, false);
        }

        chart.hideLoading();
        let haveData = aggregateData.outerGroupNames.length > 0;

        let noDataTitle = noDataMessage ? noDataMessage : 'No Data';

        this.setSubtitle(chart, haveData ? '' : noDataTitle, !haveData);
        this.redrawChart(chart);
    }

    /**
     * Populate a percent stacked area chart .
     */
    protected populateArea(chart: Highcharts.Chart, aggregateData: TransformedAggregate): void {
        chart.xAxis[0].setCategories(aggregateData.outerGroupNames);
        let numDataPoints = aggregateData.outerGroupNames.length;
        let series = [];
        for (let innerName of aggregateData.outerGroupNames) {
            let current = aggregateData.countPerOuterGroupIndex.get(innerName).find(x => x != 0);
            if (current) {
                series.push(current)
            } else {
                series.push(0)
            }
        }
        chart.addSeries({
            type: 'area',
            data: series
        }, false);

        chart.hideLoading();
        let haveData = aggregateData.outerGroupNames.length > 0;
        this.setSubtitle(chart, haveData ? '' : 'No Data', !haveData);
        this.redrawChart(chart);
    }
    /**
     * This function determines what color scheme to apply to a chart
     * based on the length of the data
     * @param arrayLength
     * @param chartType The chart type whose color scheme will be used
     */
    getAreaChartColorSystem(arrayLength: number, chartType: ChartType): string[] {
        let colors: string[];
        switch (chartType) {
            case ChartType.Bar:
            case ChartType.Column: {
                switch (arrayLength) {
                    case 1:
                        colors = Colors.STACKED_BAR_CHART_COLORS_1;
                        break;
                    case 2:
                        colors = Colors.STACKED_BAR_CHART_COLORS_2;
                        break;
                    case 3:
                        colors = Colors.STACKED_BAR_CHART_COLORS_3_PLUS;
                        break;
                    default:
                        colors = Colors.BAR_CHART_COLORS;
                }
                break;
            }
            case ChartType.Donut: 
            case ChartType.Area: {
                switch (arrayLength) {
                    case 1:
                        colors = Colors.DONUT1_CHART_COLORS;
                        break;
                    case 2:
                        colors = Colors.DONUT2_CHART_COLORS;
                        break;
                    case 3:
                        colors = Colors.DONUT3_CHART_COLORS;
                        break;
                    default:
                        colors = Colors.DONUT_CHART_COLORS;
                        break;
                }
                break;  
            }
        }
        return colors;
    }

    

    /**
     * Populate a percent stacked area chart .
     */
    protected populateStackedArea(chart: Highcharts.Chart, aggregateData: TransformedAggregate, CustomValue: boolean = false): void {
        chart.xAxis[0].setCategories(aggregateData.outerGroupNames);
        let hasData: boolean = false;

        if (aggregateData && aggregateData.innerGroupNames) {
            chart.options.colors = this.getAreaChartColorSystem(aggregateData.innerGroupNames.length, ChartType.Area);
        }
        let allSeries = new Map<string, number[]>();

        aggregateData.innerGroupNames.forEach(name => allSeries.set(name, []));

        for (let outerName of aggregateData.outerGroupNames) {


            let currentSubGroups = aggregateData.apiData.find(x => x.GroupName === outerName).SubGroups;

            for (let curr of currentSubGroups) {
                let series = allSeries.get(curr.GroupName);
                if (CustomValue) {
                    if (curr.CustomValue) {
                        series.push(curr.CustomValue);
                    }
                    else {
                        series.push(0)
                    }
                }
                else if (curr.ItemCount) {
                    series.push(curr.ItemCount)
                } else {
                    series.push(0)
                }
            }
        }

        allSeries.forEach((item, key) => {
            chart.addSeries({
                type: 'area',
                name: key,
                data: item
            })
        });

        hasData = allSeries.size > 0;

        chart.hideLoading();
        this.setSubtitle(chart, hasData ? '' : 'No Data', !hasData);
        this.redrawChart(chart);
    }



    /**
     * Transform AggregateData[] from API into a more useful structure for drawing column and bar charts.
     * This version of the method uses CustomValue
     */
    protected transformAggregateDataWithCustomValue(apiData: api.AggregateData[]): TransformedAggregate {
        let intermediate = new TransformedAggregate(apiData);


        let allSubGroups: string[] = [];
        apiData.filter(x => x.SubGroups).forEach(g => {
            let mySubGroups = g.SubGroups.map(x => x.GroupName);
            mySubGroups.forEach(function (y) {
                if (allSubGroups.indexOf(y) == -1) {
                    allSubGroups.push(y);
                }
            });
        });

        for (let outerGroupIdx = 0; outerGroupIdx < apiData.length; ++outerGroupIdx) {
            let outerGroup = apiData[outerGroupIdx];
            intermediate.outerGroupNames.push(outerGroup.GroupName);
            if (!outerGroup.SubGroups) {
                let points = intermediate.countPerOuterGroupIndex.get(outerGroup.GroupName);
                if (!points) {
                    // First time seeing this group
                    points = [0, 0, 0, 0, 0, 0];
                    intermediate.countPerOuterGroupIndex.set(outerGroup.GroupName, points);
                }

                points[outerGroupIdx] = outerGroup.CustomValue;

                continue;
            }
            // If a given record does not have a subgroup, add it. See TP-82191
            let currentGroupNames = outerGroup.SubGroups.map(y => y.GroupName);
            allSubGroups.forEach(x => {
                if (currentGroupNames.indexOf(x) < 0) {
                    outerGroup.SubGroups.push({ ItemCount: 0, CustomValue: 0, GroupName: x, SubGroups: [] });
                }
            });

            // Since the order of any given stack is irrelevant, we force the column to have single order in case the order from the server is different across widgets. See TP-82191
            let sortedGroupNames = outerGroup.SubGroups.sort((a: AggregateData, b: AggregateData) => {
                let av = a.GroupName.toLowerCase();
                let bv = b.GroupName.toLowerCase();
                let comp = av < bv ? -1 : av > bv ? 1 : 0;
                return comp;
            });

            for (let innerGroup of sortedGroupNames) {


                let points = intermediate.innerCountPerOuterGroupIndex.get(innerGroup.GroupName);
                if (!points) {
                    // First time seeing this inner group
                    intermediate.innerGroupNames.push(innerGroup.GroupName);
                    points = [0, 0, 0, 0, 0, 0];
                    intermediate.innerCountPerOuterGroupIndex.set(innerGroup.GroupName, points);
                }

                points[outerGroupIdx] = innerGroup.CustomValue;
            }
        }
        return intermediate;
    }
}

export class TransformedAggregate {
    outerGroupNames: string[] = [];
    innerGroupNames: string[] = [];
    // Used if there are subgroups
    innerCountPerOuterGroupIndex = new Map<string, number[]>();

    customLegend: string = "";

    // Used if there are no subgroups
    countPerOuterGroupIndex = new Map<string, number[]>();

    constructor(public apiData: api.AggregateData[]) {
    }
}
